import { CompaniesRepository } from '../../domain/repositories/CompaniesRepository'
import { CompaniesModel } from '../models/CompaniesModel'
import { CompaniesMiniModel } from '../models/CompaniesMiniModel'
import { CompaniesFilterModel } from '../models/CompaniesFilterModel'
import {
  CompaniesService,
  type PostCompanyAdminRequest,
  type PostPutCompanySubscriptionAdminRequest,
  type PutCompanyAdminRequest
} from '@/backend/market'
import type { CreateUpdateCompanyEntity } from '../../domain/entities/CreateUpdateCompanyEntity'
import type { CompaniesFilterEntity } from '../../domain/entities/CompaniesFilterEntity'
import type { CompaniesEntity } from '../../domain/entities/CompaniesEntity'
import type { CompaniesResponseEntity } from '../../domain/entities/CompaniesResponseEntity'
import { CreateUpdateCompanyModel } from '../models/CreateUpdateCompanyModel'
import type { CompaniesSubscriptionEntity } from '../../domain/entities/CompaniesSubscriptionEntity'
import { CompaniesSubscriptionModel } from '../models/CompaniesSubscriptionModel'
import type { CreateUpdateCompaniesSubscriptionEntity } from '../../domain/entities/CreateUpdateCompaniesSubscriptionEntity'
import { CreateUpdateCompaniesSubscriptionModel } from '../models/CreateUpdateCompaniesSubscriptionModel'
import type { CompanySubscriptionsFilterEntity } from '../../domain/entities/CompanySubscriptionsFilterEntity'
import type { CompanySubscriptionsResponseEntity } from '../../domain/entities/CompanySubscriptionsResponseEntity'
import { CompanySubscriptionsFilterModel } from '../models/CompanySubscriptionsFilterModel'
import type { CompanyPeriodsEntity } from '../../domain/entities/CompanyPeriodsEntity'
import { CompanyPeriodsModel } from '../models/CompanyPeriodsModel'
import type { UpdateCompanyPeriodsEntity } from '../../domain/entities/UpdateCompanyPeriodsEntity'
import { UpdateCompanyPeriodsModel } from '../models/UpdateCompanyPeriodsModel'
import type { CompanyBranchEntity } from '../../domain/entities/CompanyBranchEntity'
import { CompanyBranchModel } from '../models/CompanyBranchModel'
export class CompaniesRepositoryImpl implements CompaniesRepository {
  async getCompanies(
    filterData: CompaniesFilterEntity
  ): Promise<CompaniesResponseEntity | { error: true }> {
    try {
      const companiesFilterModel = new CompaniesFilterModel(filterData)
      const response = await CompaniesService.getCompaniesAdmin(companiesFilterModel)
      const companyModels = response?.items?.map((company) => new CompaniesMiniModel(company)) ?? []
      return {
        ...response,
        items: companyModels
      }
    } catch {
      return { error: true }
    }
  }

  async getCompanyById(id: number): Promise<CompaniesEntity | { error: true }> {
    try {
      const response = await CompaniesService.getCompanyAdmin({ id })
      const companyModel = new CompaniesModel(response)
      return companyModel
    } catch {
      return { error: true }
    }
  }

  async createCompany(
    data: CreateUpdateCompanyEntity
  ): Promise<{ success: true } | { error: true }> {
    try {
      const createCompanyModel = new CreateUpdateCompanyModel(data)
      await CompaniesService.postCompanyAdmin({
        requestBody: createCompanyModel as PostCompanyAdminRequest
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async updateCompany(
    id: number,
    data: CreateUpdateCompanyEntity
  ): Promise<{ success: true } | { error: true }> {
    try {
      const updateCompanyModel = new CreateUpdateCompanyModel(data)
      await CompaniesService.putCompanyAdmin({
        id,
        requestBody: updateCompanyModel as PutCompanyAdminRequest
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async deleteCompany(id: number): Promise<{ success: true } | { error: true }> {
    try {
      await CompaniesService.deleteCompanyAdmin({ id })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async getCompanySubscriptionsPaginated(
    filterData: CompanySubscriptionsFilterEntity
  ): Promise<CompanySubscriptionsResponseEntity | { error: true }> {
    try {
      const companySubscriptionsFilterModel = new CompanySubscriptionsFilterModel(filterData)
      const response = await CompaniesService.getCompanySubscriptionsAdmin(
        companySubscriptionsFilterModel
      )
      const companySubscriptionsModels =
        response.items?.map(
          (companySubscription) => new CompaniesSubscriptionModel(companySubscription)
        ) ?? []
      return {
        ...response,
        items: companySubscriptionsModels
      }
    } catch {
      return { error: true }
    }
  }

  async createCompanySubscription(
    id: number,
    data: CreateUpdateCompaniesSubscriptionEntity
  ): Promise<{ success: true } | { error: true }> {
    try {
      const createCompanySubscriptionModel = new CreateUpdateCompaniesSubscriptionModel(data)
      await CompaniesService.postCompanySubscriptionAdmin({
        id,
        requestBody: createCompanySubscriptionModel as PostPutCompanySubscriptionAdminRequest
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async updateCompanySubscription(
    id: number,
    data: CompaniesSubscriptionEntity
  ): Promise<{ success: true } | { error: true }> {
    try {
      const updateCompanySubscriptionModel = new CreateUpdateCompaniesSubscriptionModel(data)
      await CompaniesService.putCompanySubscriptionAdmin({
        id,
        requestBody: updateCompanySubscriptionModel as PostPutCompanySubscriptionAdminRequest
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async getCompanyPeriods(id: number): Promise<CompanyPeriodsEntity | { error: true }> {
    try {
      const response = await CompaniesService.getCompanyPeriodsAdmin({ id })
      const companyPeriodsModel = new CompanyPeriodsModel(response)
      return companyPeriodsModel
    } catch {
      return { error: true }
    }
  }

  async updateCompanyPeriods(
    id: number,
    data: UpdateCompanyPeriodsEntity
  ): Promise<{ success: true } | { error: true }> {
    try {
      const updateCompanyPeriodsModel = new UpdateCompanyPeriodsModel(data)
      await CompaniesService.putCompanyPeriodsAdmin({
        id,
        requestBody: updateCompanyPeriodsModel
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async getCompanyBranchs(id: number): Promise<CompanyBranchEntity[] | { error: true }> {
    try {
      const response = await CompaniesService.getCompanyBranchesAdmin({
        id
      })
      return response.branches?.map((data) => new CompanyBranchModel(data)) ?? []
    } catch {
      return { error: true }
    }
  }

  async updateCompanyBranch(
    id: number,
    data: CompanyBranchEntity[]
  ): Promise<{ success: true } | { error: true }> {
    try {
      await CompaniesService.putCompanyBranchesAdmin({
        id: id,
        requestBody: {
          branchIds: data.map((data) => data.id).filter((id) => id !== undefined)
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async disableCompany(id: number, disable: boolean): Promise<{ success: true } | { error: true }> {
    try {
      await CompaniesService.putCompanyDisabilityAdmin({
        id: id,
        requestBody: {
          disabled: disable
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
}
